import { useEffect, useState } from "react";

import {
  animate as motionAnimate,
  useMotionValue,
  useTransform,
} from "framer-motion";

import { normalizeAnimationSpeed } from "src/base-components/Chat/utils";
import { TextEditor } from "src/base-components/TextEditor/TextEditor";

type ChatTextContentProps = {
  content: string;
  delay: number;
  animate?: boolean;
  inline?: boolean;
  dataLoc?: string;
};

export const ChatTextContent: React.FC<ChatTextContentProps> = ({
  content,
  delay = 0,
  animate = false,
  inline = false,
  dataLoc,
}) => {
  const [displayTextValue, setDisplayTextValue] = useState("");
  const count = useMotionValue(0);
  useEffect(() => {
    if (animate) {
      const duration = normalizeAnimationSpeed(content.length);
      const controls = motionAnimate(count, content.length, {
        duration,
        delay,
        ease: "linear",
      });
      return controls.stop;
    }
  }, [content.length, count, delay, animate]);

  const rounded = useTransform(count, (latest) => Math.round(latest));
  const displayText = useTransform(rounded, (latest) =>
    content.slice(0, latest),
  );

  useEffect(() => {
    displayText.on("change", (value) => {
      setDisplayTextValue(value);
    });

    return () => displayText.destroy();
  }, [displayText]);

  const textToRender = animate ? displayTextValue : content;

  const animationFinishesAfter = Math.ceil(
    delay + normalizeAnimationSpeed(content.length),
  );

  const resolvedDataLoc =
    dataLoc ?? `chat-text-finishes-after-${animationFinishesAfter}`;

  if (inline) {
    return (
      <span
        className="ql-editor whitespace-pre-wrap"
        data-loc={resolvedDataLoc}
      >
        {textToRender}
      </span>
    );
  }

  return (
    <TextEditor
      dataLoc={resolvedDataLoc}
      value={textToRender}
      inline
      readOnly
    />
  );
};
